'use client';
import {
  searchPlacesForSG,
  searchZipcodeForAU,
  searchPlacesByGoogleApi,
  parseGoogleAddressByPlaceId,
} from '@castlery/modules-user-domain';
import { PureLocationSearch, SearchListOption, LocationSearchType } from './pure-search';
import { useCallback, useMemo, useState } from 'react';
import { accessInSG, accessInAU, EcEnv } from '@castlery/config';
import { useAppDispatch } from '@castlery/shared-redux-store';
import { v4 as uuidv4 } from 'uuid';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';

export { LocationSearchType } from './pure-search';

export interface ZipcodeResult {
  zipcode: string;
  city?: string;
  state?: string;
}

export interface LocationSearchProps {
  type: LocationSearchType;
  placeholder?: string;
  autoFocus?: boolean;
  onSubmit: (result: ZipcodeResult | any) => Promise<void>;
}

export function LocationSearch({ type, placeholder = '', autoFocus = false, onSubmit }: LocationSearchProps) {
  const dispatch = useAppDispatch();
  const [backupValue, setBackupValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const defaultError = 'Invalid zip code or not supported zip code';

  const addressFallbackOption: SearchListOption = {
    value: 'address_not_found_fallback',
    label: 'Can’t find your address?',
    rawData: null,
  };
  // 会话令牌
  const sessionToken = useMemo(() => uuidv4(), []);

  const auZipcodeSearchHandler = useCallback(
    async (query: string) => {
      const result = await dispatch(searchZipcodeForAU.initiate({ query }));
      if (result.error) {
        return [];
      }
      if (result.data) {
        const list = result.data.map((item) => {
          return {
            label: `${item.city}, ${item.state} ${item.zipcode}`,
            value: item.id,
            rawData: item,
          };
        });
        return list;
      }
      return [];
    },
    [dispatch]
  );

  const sgPlacesSearchHandler = useCallback(
    async (query: string) => {
      const result = await dispatch(searchPlacesForSG.initiate({ query }));
      const defaultResult = type === LocationSearchType.ADDRESS ? [addressFallbackOption] : [];
      if (result.error) {
        return [...defaultResult];
      }
      if (result.data) {
        const list = result.data.map((item) => {
          return {
            label: `${item.building_name ? item.building_name + ', ' : ''}${item.street_number} ${item.street}, ${
              item.zipcode
            }`,
            value: item.id,
            rawData: item,
          };
        });
        return [...list, ...defaultResult];
      }
      return [...defaultResult];
    },
    [dispatch, type, addressFallbackOption]
  );

  const googlePlacesSearchHandler = useCallback(
    async (query: string) => {
      const payload = {
        query,
        sessiontoken: sessionToken,
        ...(type === LocationSearchType.ZIPCODE && { type }),
      };
      const result = await dispatch(searchPlacesByGoogleApi.initiate(payload));
      const defaultResult = type === LocationSearchType.ADDRESS ? [addressFallbackOption] : [];

      if (result?.error) {
        return [...defaultResult];
      }
      if (result?.data) {
        const list = result.data.map((item) => {
          return {
            label: item.description,
            value: item.google_place_id,
            rawData: item,
          };
        });
        return [...list, ...defaultResult];
      } else if (type === LocationSearchType.ZIPCODE) {
        // 如果google api没有返回结果，则需要校验backupValue是否为有效的postcode
        const countryCode = EcEnv.NEXT_PUBLIC_COUNTRY.toUpperCase();
        const isValid = postcodeValidatorExistsForCountry(countryCode) ? postcodeValidator(query, countryCode) : true;
        if (isValid) {
          setBackupValue(query);
        }
      }
      return [...defaultResult];
    },
    [dispatch, type, sessionToken, addressFallbackOption]
  );

  const handleClear = useCallback(() => {
    setBackupValue('');
    setError('');
  }, []);

  const searchHandler = useCallback(
    async (query: string) => {
      if (!query) return [];
      handleClear();
      if (type === LocationSearchType.ZIPCODE && query.length >= 3) {
        // au 在查zipcode时，使用定制的API，sg无查询zipcode的场景，其他市场使用google api
        const search = accessInAU ? auZipcodeSearchHandler : googlePlacesSearchHandler;
        const result = await search(query);
        if (result.length <= 0) {
          setError(defaultError);
        }
        return result;
      }
      // sg查询地址使用定制API， 其他市场使用google api
      const search = accessInSG ? sgPlacesSearchHandler : googlePlacesSearchHandler;
      const result = await search(query);
      if (result.length <= 0) {
        setError(defaultError);
      }
      return result;
    },
    [type, auZipcodeSearchHandler, sgPlacesSearchHandler, googlePlacesSearchHandler, handleClear]
  );

  const onSelect = useCallback(
    async (option: SearchListOption) => {
      if (!option.value) return Promise.resolve();
      let selectedOption = option.rawData;
      let result = null;
      if (option.rawData?.google_place_id) {
        const res = await dispatch(
          parseGoogleAddressByPlaceId.initiate({ googlePlaceId: option.value, sessiontoken: sessionToken })
        );
        if (res.error) {
          return Promise.resolve();
        }

        if (res.data) {
          selectedOption = {
            ...selectedOption,
            ...res.data,
          };
        }
      }
      if (type === LocationSearchType.ZIPCODE) {
        result = {
          zipcode: selectedOption?.zipcode ?? '',
          city: selectedOption?.city ?? '',
          state: selectedOption?.state ?? selectedOption?.state_name ?? '',
        };
      } else if (type === LocationSearchType.ADDRESS) {
        result = {
          ...selectedOption,
        };
      }
      return result;
    },
    [dispatch, type, sessionToken]
  );

  const handleSubmit = async (payload: any) => {
    if (type === LocationSearchType.ZIPCODE) {
      const zipcodeResult = payload || {
        zipcode: backupValue,
        city: '',
        state: '',
      };
      if (!zipcodeResult.zipcode) {
        return;
      }
      await onSubmit(zipcodeResult);
      return;
    }
    if (type === LocationSearchType.ADDRESS) {
      await onSubmit(payload);
      return;
    }
  };

  return (
    <>
      <PureLocationSearch
        type={type}
        placeholder={placeholder}
        autoFocus={autoFocus}
        searchHandler={searchHandler}
        onSelect={onSelect}
        onSubmit={handleSubmit}
        errorHelperText={error}
        onClear={handleClear}
      />
    </>
  );
}
